// Export an event's stock — one row per lot — as .xlsx.
//
// This is the sheet someone takes to the shelf to count. Every column is the
// raw allocation figure; nothing is formatted, so the same file can be pasted
// into a stock-take spreadsheet without stripping "Rp" or thousand separators.
// Same recipe as productsExport.ts: SheetJS through loadXlsx(), widths sized to
// the content, header row frozen.

import { loadXlsx } from '@/lib/spreadsheet';
import { buildStockWorkbook } from '@/lib/importTemplates';
import type { EventStockAllocation, Product } from '@/types/domain';

export const EVENT_STOCK_COLUMNS = [
  'SKU', 'Produk', 'Lot', 'Expired date', 'Satuan', 'Form order',
  'Qty in', 'Terjual', 'Sisa', 'Kembali gudang',
] as const;

const isoDate = (ms?: number) => (ms ? new Date(ms).toISOString().slice(0, 10) : '');

export function eventStockRows(allocations: EventStockAllocation[]): (string | number)[][] {
  const sorted = [...allocations].sort(
    (a, b) =>
      a.productName.localeCompare(b.productName) ||
      (a.lotNumber || '').localeCompare(b.lotNumber || '') ||
      (a.expiryDate ?? 0) - (b.expiryDate ?? 0),
  );
  return sorted.map((a) => [
    a.productSku,
    a.productName,
    a.lotNumber || '',
    isoDate(a.expiryDate),
    a.unit,
    a.orderForm || '',
    a.qtyIn,
    a.qtySold,
    Math.max(0, a.qtyIn - a.qtySold - a.qtyReturnedToGudang),
    a.qtyReturnedToGudang,
  ]);
}

export async function buildEventStockXlsx(
  event: { name: string; code: string },
  allocations: EventStockAllocation[],
  /**
   * The active catalogue. When given, the file also carries an "Untuk import"
   * sheet (current lots prefilled, SKU dropdown), the product list and the
   * instructions — so the same export is both the stock report and the sheet
   * to edit and import back.
   */
  products?: ReadonlyArray<Product>,
): Promise<Blob> {
  const XLSX = await loadXlsx();
  const rows = eventStockRows(allocations);
  const totals: (string | number)[] = [
    'TOTAL', '', '', '', '', '',
    allocations.reduce((s, a) => s + a.qtyIn, 0),
    allocations.reduce((s, a) => s + a.qtySold, 0),
    allocations.reduce((s, a) => s + Math.max(0, a.qtyIn - a.qtySold - a.qtyReturnedToGudang), 0),
    allocations.reduce((s, a) => s + a.qtyReturnedToGudang, 0),
  ];
  const aoa: (string | number)[][] = [[...EVENT_STOCK_COLUMNS], ...rows, totals];
  const reportCols = aoa[0].map((_, i) => {
    const w = aoa.reduce((m, r) => Math.max(m, String(r[i] ?? '').length), 0);
    return { wch: Math.min(Math.max(w + 2, 8), 48) };
  });
  // Sheet names are capped at 31 chars and may not contain []:*?/\
  const reportName = `Stok ${event.code}`.replace(/[[\]:*?/\\]/g, '-').slice(0, 31);

  if (products) {
    const out = await buildStockWorkbook(products, {
      allocations,
      eventCode: event.code,
      report: { name: reportName, aoa, cols: reportCols },
    });
    return new Blob([out], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = aoa[0].map((_, i) => {
    const w = aoa.reduce((m, r) => Math.max(m, String(r[i] ?? '').length), 0);
    return { wch: Math.min(Math.max(w + 2, 8), 48) };
  });
  ws['!freeze'] = { xSplit: 0, ySplit: 1 };

  const wb = XLSX.utils.book_new();
  // Sheet names are capped at 31 chars and may not contain []:*?/\
  const sheet = `Stok ${event.code}`.replace(/[[\]:*?/\\]/g, '-').slice(0, 31);
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

export const eventStockFilename = (event: { code: string }) =>
  `stok-${event.code}-${new Date().toISOString().slice(0, 10)}.xlsx`;
